<template>
    <section class="command-reference">
        <b-input
            class="command-search"
            v-model="search"
            icon="search"
            icon-pack="fas"
            placeholder="Search commands..."
            expanded />

        <div class="category-group" v-for="category in categories" :key="category.key" v-show="category.commands.length">
            <b-collapse class="category-collapse" :open="openCategories[category.key]" @open="$set(openCategories, category.key, true)" @close="$set(openCategories, category.key, false)">
                <template #trigger="props">
                    <div class="category-trigger">
                        <span class="category-label">{{ category.label }}</span>
                        <span class="category-count">{{ category.commands.length }}</span>
                        <b-icon class="chevron" :icon="props.open ? 'caret-up' : 'caret-down'" pack="fas" />
                    </div>
                </template>

                <div class="command-list">
                    <div class="command-row" v-for="command in category.commands" :key="command.name" @click="toggle(command.name)">
                        <div class="command-row-head">
                            <code class="command-usage">{{ prefix }}{{ command.usage }}</code>
                            <span class="command-description">{{ command.description }}</span>
                            <b-icon class="chevron" :icon="openCommands[command.name] ? 'caret-up' : 'caret-down'" pack="fas" size="is-small" />
                        </div>
                        <div class="command-row-detail" v-if="openCommands[command.name]">
                            <div class="detail-item">
                                <strong>Aliases</strong>
                                <span>{{ (command.aliases || []).join(', ') || 'None' }}</span>
                            </div>
                            <div class="detail-item" v-if="(command.args || []).length">
                                <strong>Options</strong>
                                <span>
                                    <code v-for="arg in command.args" :key="arg.name">{{ arg.name }}{{ arg.required ? '*' : '' }}</code>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </b-collapse>
        </div>

        <p class="empty-state" v-if="loading">Loading commands...</p>
        <p class="empty-state" v-else-if="!filteredCommands.length && search">No commands match "{{ search }}".</p>
        <p class="empty-state" v-else-if="!filteredCommands.length">Commands are temporarily unavailable. Try refreshing the page.</p>
    </section>
</template>

<style lang="scss" scoped>
.command-reference {
    text-align: left;
    max-width: 800px;
    margin: 0 auto;

    .command-search {
        margin-bottom: 20px;
    }

    .category-group {
        margin-bottom: 12px;
        background: white;
        border-radius: 10px;
        overflow: hidden;
        box-shadow: 0 2px 10px rgba(0,0,0,0.06);
    }

    .category-trigger {
        display: flex;
        align-items: center;
        padding: 16px 20px;
        cursor: pointer;
        font-weight: bold;
        text-transform: capitalize;

        .category-count {
            margin-left: 8px;
            color: $gray;
            font-weight: normal;
            font-size: 0.85rem;
        }

        .chevron {
            margin-left: auto;
            color: $gray;
        }
    }

    .command-list {
        border-top: 1px solid $gray-light;
    }

    .command-row {
        padding: 12px 20px;
        border-bottom: 1px solid $gray-light;
        cursor: pointer;

        &:last-child {
            border-bottom: none;
        }

        .command-row-head {
            display: flex;
            align-items: center;
            gap: 12px;

            .command-usage {
                flex-shrink: 0;
                background: $primary-light;
                color: $primary-dark;
                padding: 2px 8px;
                border-radius: 4px;
                font-weight: bold;
            }

            .command-description {
                flex-grow: 1;
                color: $gray-dark;
                font-size: 0.9rem;
            }

            .chevron {
                color: $gray;
            }
        }

        .command-row-detail {
            margin-top: 10px;
            padding-left: 4px;
            font-size: 0.85rem;

            .detail-item {
                margin-bottom: 6px;
                strong {
                    display: inline-block;
                    min-width: 80px;
                    color: $gray-dark;
                }
                code {
                    background: $gray-light;
                    border-radius: 3px;
                    padding: 1px 6px;
                    margin-right: 4px;
                }
            }
        }
    }

    .empty-state {
        text-align: center;
        color: $gray;
        padding: 40px 0;
    }
}
</style>

<script>
const CATEGORY_LABELS = {
    fun: 'Fun',
    economy: 'Economy',
    info: 'Info',
}

const CATEGORY_ORDER = ['fun', 'economy', 'info']

export default {
    name: 'CommandReference',
    data() {
        return {
            data: [],
            loading: true,
            prefix: '/',
            search: '',
            openCategories: { fun: true, economy: true, info: true },
            openCommands: {},
        }
    },
    computed: {
        filteredCommands() {
            let term = this.search.trim().toLowerCase()
            if (!term) return this.data
            return this.data.filter(command =>
                command.name.toLowerCase().includes(term) ||
                command.description.toLowerCase().includes(term) ||
                (command.aliases || []).some(a => a.toLowerCase().includes(term))
            )
        },
        categories() {
            let byCategory = {}
            for (const command of this.filteredCommands) {
                if (!byCategory[command.category]) byCategory[command.category] = []
                byCategory[command.category].push(command)
            }
            return CATEGORY_ORDER.map(key => ({
                key,
                label: CATEGORY_LABELS[key] || key,
                commands: (byCategory[key] || []).sort((a, b) => a.name.localeCompare(b.name)),
            }))
        },
    },
    methods: {
        toggle(name) {
            this.$set(this.openCommands, name, !this.openCommands[name])
        },
        async loadData() {
            try {
                let res = await fetch('/api/fetchCommands', {
                    method: 'GET',
                    headers: {
                        authorization: 'gamebot!'
                    }
                })
                this.data = await res.json()
            } catch (e) {
                this.data = []
            } finally {
                this.loading = false
            }
        }
    },
    mounted() {
        this.loadData()
    }
}
</script>
